import React, { useState, useMemo, useEffect, useRef } from 'react';
import { db } from '../data/store';

// ── Constants ─────────────────────────────────────────────────────────────────
const RUSH_COLORS = { 1:'#b83232', 2:'#e05500', 3:'#e09900', 4:'#4caf50', 5:'#1e3060' };
const TYPE_COLORS = {
  Fuel:'#e09900', Specific:'#1a6b42', Water:'#1a7dc4', Coal:'#555',
  Stormwater:'#2196f3', NPDES:'#9c27b0', TCLP:'#f44336', Default:'#1e3060'
};
const tc = (t) => TYPE_COLORS[t] || TYPE_COLORS.Default;

const STAGES = [
  { id:'received',   label:'Received',        icon:'📥', color:'#1a7dc4' },
  { id:'inprogress', label:'In Progress',      icon:'🔬', color:'#e09900' },
  { id:'ready',      label:'Ready to Report',  icon:'📄', color:'#9c27b0' },
  { id:'complete',   label:'Complete',         icon:'✅', color:'#1a6b42' },
];
const STAGE_IDS = STAGES.map(s => s.id);

// Auto-detect stage from data (used if no manual override)
function autoStage(s) {
  if (s.completed) return 'complete';
  if (s.results && Object.keys(s.results).length > 0) return 'ready';
  if (s.params && s.params.length > 0) return 'inprogress';
  return 'received';
}

// Effective stage: manual override wins, else auto
function getStage(s) {
  return s.boardStage || autoStage(s);
}

// Move a sample to a new stage, persist to db
function moveToStage(sampleId, newStage) {
  const updates = { boardStage: newStage };
  if (newStage === 'complete') {
    updates.completed = true;
    updates.dateCompleted = new Date().toISOString().split('T')[0];
  } else {
    // Moving back from complete resets completion
    const s = db.getSamples().find(x => String(x.id) === String(sampleId));
    if (s?.completed && newStage !== 'complete') {
      updates.completed = false;
      updates.dateCompleted = '';
    }
  }
  db.updateSample(sampleId, updates);
}

function daysUntilDue(dueDate) {
  if (!dueDate) return null;
  return Math.ceil((new Date(dueDate) - new Date()) / 86400000);
}

function DueTag({ dueDate }) {
  const days = daysUntilDue(dueDate);
  if (days === null) return null;
  const overdue = days < 0;
  const urgent  = days === 0;
  const soon    = days <= 2;
  const bg  = overdue ? '#b83232' : urgent ? '#e05500' : soon ? '#e09900' : '#e8ecf8';
  const col = (overdue||urgent||soon) ? 'white' : '#555';
  const label = overdue ? `${Math.abs(days)}d OVERDUE` : urgent ? 'DUE TODAY' : `${days}d left`;
  return (
    <span style={{background:bg,color:col,fontSize:9,fontWeight:700,
      padding:'1px 6px',borderRadius:10,whiteSpace:'nowrap'}}>{label}</span>
  );
}

// ── Sample Card ───────────────────────────────────────────────────────────────
function SampleCard({ s, compact, onMove, onNavigate, onSelectSample, isDragging }) {
  const days     = daysUntilDue(s.dueDate);
  const overdue  = days !== null && days < 0;
  const rush     = s.rush ? Math.min(parseInt(s.rush), 5) : null;
  const paramCnt = s.params?.length || 0;
  const resCnt   = Object.keys(s.results || {}).length;
  const stage    = getStage(s);
  const stageIdx = STAGE_IDS.indexOf(stage);

  const border = overdue ? '#b83232' : rush && rush <= 2 ? RUSH_COLORS[rush] : '#e2e6f0';

  return (
    <div
      draggable
      onDragStart={e => {
        e.dataTransfer.setData('sampleId', String(s.id));
        e.dataTransfer.effectAllowed = 'move';
      }}
      style={{
        background: isDragging ? '#e8f0ff' : 'white',
        borderRadius:6, padding: compact ? '6px 8px' : '10px 12px',
        border:`1px solid ${border}`, borderLeft:`3px solid ${tc(s.testType)}`,
        boxShadow: overdue ? '0 2px 8px rgba(184,50,50,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
        marginBottom:6, opacity: isDragging ? 0.5 : 1,
        transition:'box-shadow 0.15s, opacity 0.15s',
        userSelect:'none',
      }}
    >
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:compact?2:4}}>
        <span style={{fontFamily:'monospace',fontWeight:700,fontSize:12,
          color:'#1e3060',cursor:'pointer',textDecoration:'underline dotted'}}
          onClick={()=>{ onSelectSample(s); onNavigate('setup'); }}>
          {s.id}
        </span>
        {rush && (
          <span style={{background:RUSH_COLORS[rush],color:'white',
            fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:10}}>R{rush}</span>
        )}
        <span style={{background:tc(s.testType)+'22',color:tc(s.testType),
          fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:10}}>
          {s.testType}
        </span>
        <div style={{flex:1}}/>
        <DueTag dueDate={s.dueDate}/>
      </div>

      {/* Company / source */}
      <div style={{fontSize:11,fontWeight:600,color:'#1e3060',
        overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',
        marginBottom:compact?0:3}}>
        {s.source||s.client||''}
      </div>

      {!compact && s.desc && (
        <div style={{fontSize:10,color:'#888',overflow:'hidden',
          textOverflow:'ellipsis',whiteSpace:'nowrap',marginBottom:5}}>
          {s.desc}
        </div>
      )}

      {!compact && (
        <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6,flexWrap:'wrap'}}>
          <span style={{fontSize:10,color:'#888'}}>
            WO: <strong style={{color:'#555'}}>{s.workOrder}</strong>
          </span>
          {s.tech && (
            <span style={{fontSize:10,color:'#888'}}>
              Tech: <strong style={{color:'#555'}}>{s.tech}</strong>
            </span>
          )}
          {paramCnt>0 && (
            <span style={{fontSize:10,background:'#f0f4ff',color:'#1e3060',
              padding:'1px 5px',borderRadius:8}}>{paramCnt}p</span>
          )}
          {resCnt>0 && (
            <span style={{fontSize:10,background:'#d4edda',color:'#155724',
              padding:'1px 5px',borderRadius:8}}>{resCnt}r</span>
          )}
        </div>
      )}

      {/* Move buttons */}
      <div style={{display:'flex',gap:3,marginTop:compact?4:0}}>
        {stageIdx > 0 && (
          <button
            onClick={e=>{e.stopPropagation();onMove(s.id, STAGE_IDS[stageIdx-1]);}}
            title={`Move to ${STAGES[stageIdx-1].label}`}
            style={{flex:1,background:'#f0f2f8',border:'1px solid #dde1ec',
              borderRadius:3,padding:'3px 0',fontSize:10,cursor:'pointer',
              color:'#555',fontWeight:500,display:'flex',alignItems:'center',
              justifyContent:'center',gap:3}}>
            ← {STAGES[stageIdx-1].icon}
          </button>
        )}
        {stageIdx < STAGE_IDS.length-1 && (
          <button
            onClick={e=>{e.stopPropagation();onMove(s.id, STAGE_IDS[stageIdx+1]);}}
            title={`Move to ${STAGES[stageIdx+1].label}`}
            style={{flex:1,background:'#1e3060',border:'1px solid #1e3060',
              borderRadius:3,padding:'3px 0',fontSize:10,cursor:'pointer',
              color:'white',fontWeight:600,display:'flex',alignItems:'center',
              justifyContent:'center',gap:3}}>
            {STAGES[stageIdx+1].icon} →
          </button>
        )}
      </div>
    </div>
  );
}

function LoginQueueCard({ item, onRemove }) {
  return (
    <div style={{background:'#eef7ff',borderRadius:6,padding:'9px 10px',
      border:'1px dashed #1a7dc4',borderLeft:'3px solid #1a7dc4',marginBottom:6}}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
        <span style={{background:'#1a7dc4',color:'white',fontSize:9,fontWeight:700,
          padding:'2px 6px',borderRadius:10}}>AWAITING LOGIN</span>
        <span style={{marginLeft:'auto',fontSize:10,fontWeight:700,color:'#1e3060'}}>
          {item.sampleCount} sample{Number(item.sampleCount) === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{fontSize:12,fontWeight:700,color:'#1e3060',marginBottom:3}}>
        {item.company}
      </div>
      <div style={{fontSize:10,color:'#667085',marginBottom:7}}>
        Received: {item.receivedDate || '—'}
      </div>
      <button onClick={() => onRemove(item)} style={{width:'100%',background:'#1a6b42',
        border:'1px solid #1a6b42',borderRadius:3,padding:'4px 6px',fontSize:10,
        cursor:'pointer',color:'white',fontWeight:700}}>
        ✓ Samples Logged In
      </button>
    </div>
  );
}

function PendingInvoiceCard({ workOrder, onNavigate }) {
  const sampleCount = workOrder.samples.length;
  const unbilledCount = workOrder.samples.filter(s => !s.billed).length;
  const company = workOrder.samples.find(s => s.source)?.source ||
    workOrder.samples.find(s => s.client)?.client || '';
  const completedDates = workOrder.samples.map(s => s.dateCompleted).filter(Boolean).sort();
  const completedDate = completedDates[completedDates.length - 1] || '';

  return (
    <div style={{background:'#f1fbf5',borderRadius:6,padding:'9px 10px',
      border:'1px solid #b9dfc9',borderLeft:'3px solid #1a6b42',marginBottom:6}}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
        <span style={{background:'#1a6b42',color:'white',fontSize:9,fontWeight:700,
          padding:'2px 6px',borderRadius:10}}>PENDING INVOICE</span>
        <span style={{marginLeft:'auto',fontSize:10,fontWeight:700,color:'#1e3060'}}>
          {unbilledCount} unbilled
        </span>
      </div>
      <div style={{fontFamily:'monospace',fontSize:13,fontWeight:800,
        color:'#1e3060',marginBottom:3}}>
        WO: {workOrder.workOrder}
      </div>
      <div style={{fontSize:11,fontWeight:700,color:'#1e3060',marginBottom:3,
        overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
        {company}
      </div>
      <div style={{fontSize:10,color:'#667085',marginBottom:7}}>
        {sampleCount} sample{sampleCount === 1 ? '' : 's'}
        {completedDate ? ` · Completed ${completedDate}` : ''}
      </div>
      <button onClick={() => onNavigate('invoice')} style={{width:'100%',background:'#1e3060',
        border:'1px solid #1e3060',borderRadius:3,padding:'4px 6px',fontSize:10,
        cursor:'pointer',color:'white',fontWeight:700}}>
        Create Invoice →
      </button>
    </div>
  );
}

// ── Kanban Column ─────────────────────────────────────────────────────────────
function KanbanCol({ stage, cards, queueItems = [], invoiceWorkOrders = [], compact, onMove, onNavigate, onSelectSample, onRemoveQueue, dragOver, onDragOver, onDragLeave, onDrop }) {
  const itemCount = stage.id === 'complete'
    ? invoiceWorkOrders.length
    : cards.length + queueItems.reduce((sum, item) => sum + (Number(item.sampleCount) || 0), 0);
  return (
    <div style={{display:'flex',flexDirection:'column',borderRadius:8,overflow:'hidden',
      border:`1px solid ${stage.color}44`,
      boxShadow: dragOver ? `0 0 0 2px ${stage.color}` : 'none',
      transition:'box-shadow 0.15s'}}>
      {/* Column header */}
      <div style={{background:stage.color,color:'white',padding:'8px 12px',
        display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
        <span style={{fontSize:16}}>{stage.icon}</span>
        <span style={{fontWeight:700,fontSize:12}}>{stage.label}</span>
        <span style={{marginLeft:'auto',background:'rgba(255,255,255,0.25)',
          borderRadius:10,padding:'1px 8px',fontSize:11,fontWeight:700}}>
          {itemCount}
        </span>
      </div>
      {/* Drop zone */}
      <div
        style={{flex:1,overflowY:'auto',padding:'8px',
          background: dragOver ? `${stage.color}11` : '#f4f6fb',
          minHeight:80, transition:'background 0.15s'}}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}>
        {cards.length===0 && queueItems.length===0 && invoiceWorkOrders.length===0 && !dragOver && (
          <div style={{textAlign:'center',color:'#ccc',fontSize:11,
            padding:'20px 8px',fontStyle:'italic',pointerEvents:'none'}}>
            Drop here or use → buttons
          </div>
        )}
        {queueItems.map(item => (
          <LoginQueueCard key={item.id} item={item} onRemove={onRemoveQueue}/>
        ))}
        {invoiceWorkOrders.map(workOrder => (
          <PendingInvoiceCard key={workOrder.workOrder} workOrder={workOrder}
            onNavigate={onNavigate}/>
        ))}
        {cards.map(s=>(
          <SampleCard key={s.id} s={s} compact={compact}
            onMove={onMove} onNavigate={onNavigate} onSelectSample={onSelectSample}/>
        ))}
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function LabBoard({ onNavigate, onSelectSample }) {
  const [view,         setView]         = useState('kanban');
  const [compact,      setCompact]      = useState(false);
  const [techFilter,   setTechFilter]   = useState('all');
  const [typeFilter,   setTypeFilter]   = useState('all');
  const [showComplete, setShowComplete] = useState(false);
  const [samples,      setSamples]      = useState(()=>db.getSamples());
  const [loginQueue,   setLoginQueue]   = useState(()=>db.getLoginQueue());
  const [dragOverCol,  setDragOverCol]  = useState(null);
  const [tick,         setTick]         = useState(0);

  // Auto-refresh every 30s
  useEffect(()=>{
    const refreshAll = () => {
      setSamples(db.getSamples());
      setLoginQueue(db.getLoginQueue());
      setTick(n=>n+1);
    };
    const t = setInterval(refreshAll, 30000);
    window.addEventListener('lims-login-queue-changed', refreshAll);
    return ()=>{
      clearInterval(t);
      window.removeEventListener('lims-login-queue-changed', refreshAll);
    };
  },[]);

  const refresh = () => {
    setSamples(db.getSamples());
    setLoginQueue(db.getLoginQueue());
  };

  const handleRemoveQueue = async item => {
    if (!window.confirm(`Remove ${item.company} after confirming all ${item.sampleCount} sample${Number(item.sampleCount) === 1 ? '' : 's'} were logged in?`)) return;
    await db.removeLoginQueueItem(item.id);
    refresh();
  };

  // Move handler
  const handleMove = (sampleId, newStage) => {
    moveToStage(sampleId, newStage);
    refresh();
  };

  // Drag-and-drop handlers
  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(stageId);
  };
  const handleDrop = (e, stageId) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('sampleId').trim();
    if (id) handleMove(id, stageId);
    setDragOverCol(null);
  };

  // Filters
  const working = useMemo(()=>{
    let s = samples.filter(x => showComplete ? true : !x.completed || getStage(x)!=='complete');
    if (showComplete) s = samples;
    else s = samples.filter(x => getStage(x) !== 'complete' || x.boardStage === 'complete');
    // Actually: show non-complete always; show complete only if toggled
    s = samples.filter(x => {
      if (x.completed && !showComplete) return false;
      return true;
    });
    if (techFilter !== 'all') s = s.filter(x=>(x.tech||'unassigned')===techFilter);
    if (typeFilter !== 'all') s = s.filter(x=>x.testType===typeFilter);
    return s;
  },[samples,showComplete,techFilter,typeFilter]);

  // Kanban columns
  const kanbanCols = useMemo(()=>{
    const cols = {}; STAGE_IDS.forEach(id=>{cols[id]=[];});
    working.forEach(s=>{ const st=getStage(s); if(cols[st]) cols[st].push(s); });
    Object.values(cols).forEach(arr=>arr.sort((a,b)=>{
      const da=daysUntilDue(a.dueDate)??999, db2=daysUntilDue(b.dueDate)??999;
      const ra=a.rush?parseInt(a.rush):6, rb=b.rush?parseInt(b.rush):6;
      return da!==db2 ? da-db2 : ra-rb;
    }));
    return cols;
  },[working]);

  // Complete is an invoicing queue, grouped by work order instead of sample.
  // A work order appears only after every sample is complete, and remains until
  // every sample has been billed or linked to an invoice.
  const pendingInvoiceWorkOrders = useMemo(()=>{
    const groups = new Map();
    samples.forEach(sample => {
      const workOrder = String(sample.workOrder || '').trim() || `Sample ${sample.id}`;
      if (!groups.has(workOrder)) groups.set(workOrder, []);
      groups.get(workOrder).push(sample);
    });
    return [...groups.entries()]
      .filter(([, workOrderSamples]) =>
        workOrderSamples.length > 0 &&
        workOrderSamples.every(sample => sample.completed) &&
        workOrderSamples.some(sample => !sample.billed)
      )
      .map(([workOrder, workOrderSamples]) => ({ workOrder, samples: workOrderSamples }))
      .sort((a,b) => {
        const aDates = a.samples.map(s=>s.dateCompleted||'').sort();
        const bDates = b.samples.map(s=>s.dateCompleted||'').sort();
        const aDate = aDates[aDates.length - 1] || '';
        const bDate = bDates[bDates.length - 1] || '';
        return aDate.localeCompare(bDate) || a.workOrder.localeCompare(b.workOrder);
      });
  },[samples]);

  // Priority list
  const priorityList = useMemo(()=>[...working].sort((a,b)=>{
    const da=daysUntilDue(a.dueDate)??999, db2=daysUntilDue(b.dueDate)??999;
    return da!==db2?da-db2:(a.rush??6)-(b.rush??6);
  }),[working]);

  // By tech
  const byTech = useMemo(()=>{
    const map=new Map();
    working.forEach(s=>{
      const t=s.tech||'Unassigned';
      if(!map.has(t)) map.set(t,[]);
      map.get(t).push(s);
    });
    return [...map.entries()].sort((a,b)=>a[0].localeCompare(b[0]));
  },[working]);

  // Stats
  const activeAll = samples.filter(s=>!s.completed);
  const stats = {
    active: activeAll.length,
    overdue: activeAll.filter(s=>daysUntilDue(s.dueDate)!==null&&daysUntilDue(s.dueDate)<0).length,
    dueToday: activeAll.filter(s=>daysUntilDue(s.dueDate)===0).length,
    rush: activeAll.filter(s=>s.rush&&parseInt(s.rush)<=2).length,
  };

  const techs = ['all',...new Set(activeAll.map(s=>s.tech||'unassigned'))];
  const types = ['all',...new Set(activeAll.map(s=>s.testType).filter(Boolean))];

  const btnV = (v) => ({
    padding:'5px 11px',borderRadius:4,border:'1px solid #cdd3e4',fontSize:11,
    cursor:'pointer',fontWeight:600,
    background:view===v?'#1e3060':'white',color:view===v?'white':'#1e3060',
  });

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>

      {/* Toolbar */}
      <div style={{background:'white',borderBottom:'1px solid #e2e6f0',
        padding:'7px 14px',display:'flex',gap:8,alignItems:'center',
        flexWrap:'wrap',flexShrink:0}}>

        <div style={{display:'flex',gap:2,background:'#f0f2f8',borderRadius:5,padding:2}}>
          {[['kanban','🗂 Kanban'],['priority','⚡ Priority'],['tech','👤 By Tech']].map(([v,l])=>(
            <button key={v} onClick={()=>setView(v)} style={{...btnV(v),border:'none',borderRadius:4}}>
              {l}
            </button>
          ))}
        </div>

        <div style={{width:1,height:20,background:'#ddd'}}/>

        <select value={techFilter} onChange={e=>setTechFilter(e.target.value)}
          style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 8px',
            fontSize:11,outline:'none'}}>
          {techs.map(t=><option key={t} value={t}>{t==='all'?'All Techs':t}</option>)}
        </select>

        <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}
          style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 8px',
            fontSize:11,outline:'none'}}>
          {types.map(t=><option key={t} value={t}>{t==='all'?'All Types':t}</option>)}
        </select>

        <label style={{display:'flex',alignItems:'center',gap:5,fontSize:11,
          cursor:'pointer',fontWeight:500}}>
          <input type="checkbox" checked={showComplete}
            onChange={e=>setShowComplete(e.target.checked)}
            style={{accentColor:'#1e3060'}}/>
          Show completed
        </label>

        <label style={{display:'flex',alignItems:'center',gap:5,fontSize:11,
          cursor:'pointer',fontWeight:500}}>
          <input type="checkbox" checked={compact}
            onChange={e=>setCompact(e.target.checked)}
            style={{accentColor:'#1e3060'}}/>
          Compact
        </label>

        <div style={{flex:1}}/>

        {stats.overdue>0 && (
          <span style={{background:'#b83232',color:'white',fontSize:11,fontWeight:700,
            padding:'3px 10px',borderRadius:10}}>
            🚨 {stats.overdue} Overdue
          </span>
        )}
        {stats.dueToday>0 && (
          <span style={{background:'#e05500',color:'white',fontSize:11,fontWeight:700,
            padding:'3px 10px',borderRadius:10}}>
            ⏰ {stats.dueToday} Due Today
          </span>
        )}
        {stats.rush>0 && (
          <span style={{background:'#e09900',color:'white',fontSize:11,fontWeight:700,
            padding:'3px 10px',borderRadius:10}}>
            ⚡ {stats.rush} Rush
          </span>
        )}
        <span style={{fontSize:11,color:'#888'}}>{stats.active} active</span>

        <button className="btn btn-primary" style={{fontSize:11}}
          onClick={()=>onNavigate('receive')}>＋ Receive</button>
      </div>

      {/* ── Instruction strip for kanban ── */}
      {view==='kanban' && (
        <div style={{background:'#f0f4ff',borderBottom:'1px solid #dde4f0',
          padding:'4px 14px',fontSize:11,color:'#1e3060',display:'flex',
          alignItems:'center',gap:8,flexShrink:0}}>
          <span>💡</span>
          <span><strong>Drag</strong> a card to another column, or use the <strong>← →</strong> buttons on each card to move it one step.</span>
        </div>
      )}

      {/* Main content */}
      <div style={{flex:1,overflow:'hidden',background:'#eef0f5'}}>

        {/* ── KANBAN ── */}
        {view==='kanban' && (
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,
            height:'100%',padding:12,boxSizing:'border-box'}}>
            {STAGES.map(stage=>(
              <KanbanCol
                key={stage.id}
                stage={stage}
                cards={stage.id === 'complete' ? [] : (kanbanCols[stage.id]||[])}
                queueItems={stage.id === 'received' ? loginQueue : []}
                invoiceWorkOrders={stage.id === 'complete' ? pendingInvoiceWorkOrders : []}
                compact={compact}
                onMove={handleMove}
                onNavigate={onNavigate}
                onSelectSample={onSelectSample}
                onRemoveQueue={handleRemoveQueue}
                dragOver={dragOverCol===stage.id}
                onDragOver={e=>handleDragOver(e,stage.id)}
                onDragLeave={()=>setDragOverCol(null)}
                onDrop={e=>handleDrop(e,stage.id)}
              />
            ))}
          </div>
        )}

        {/* ── PRIORITY ── */}
        {view==='priority' && (
          <div style={{height:'100%',overflowY:'auto',padding:12}}>
            <div style={{maxWidth:900,margin:'0 auto'}}>
              {[
                {label:'🚨 Overdue',       filter:s=>daysUntilDue(s.dueDate)!==null&&daysUntilDue(s.dueDate)<0,  bg:'#fff0f0',border:'#b83232'},
                {label:'⏰ Due Today',      filter:s=>daysUntilDue(s.dueDate)===0,                                bg:'#fff5ed',border:'#e05500'},
                {label:'⚡ Due in 1-2 Days',filter:s=>{const d=daysUntilDue(s.dueDate);return d!==null&&d>0&&d<=2;},bg:'#fffbee',border:'#e09900'},
                {label:'📋 Due in 3-7 Days',filter:s=>{const d=daysUntilDue(s.dueDate);return d!==null&&d>2&&d<=7;},bg:'#f0f4ff',border:'#1a7dc4'},
                {label:'📌 Later / No Due', filter:s=>{const d=daysUntilDue(s.dueDate);return d===null||d>7;},     bg:'#f8f9ff',border:'#cdd3e4'},
              ].map(group=>{
                const items=priorityList.filter(group.filter);
                if(items.length===0) return null;
                return (
                  <div key={group.label} style={{marginBottom:14}}>
                    <div style={{fontWeight:700,fontSize:12,color:'#1e3060',
                      marginBottom:6,display:'flex',alignItems:'center',gap:8}}>
                      {group.label}
                      <span style={{fontSize:11,fontWeight:400,color:'#888'}}>({items.length})</span>
                    </div>
                    <div style={{background:group.bg,borderRadius:6,padding:8,
                      border:`1px solid ${group.border}33`}}>
                      {items.map(s=>(
                        <SampleCard key={s.id} s={s} compact={compact}
                          onMove={handleMove} onNavigate={onNavigate}
                          onSelectSample={onSelectSample}/>
                      ))}
                    </div>
                  </div>
                );
              })}
              {priorityList.length===0 && (
                <div style={{textAlign:'center',color:'#999',padding:40,fontSize:14}}>
                  🎉 No active samples in the lab
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── BY TECH ── */}
        {view==='tech' && (
          <div style={{height:'100%',overflowY:'auto',padding:12}}>
            <div style={{display:'grid',
              gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',
              gap:12,alignItems:'start'}}>
              {byTech.map(([tech,techSamples])=>{
                const overdueN=techSamples.filter(s=>daysUntilDue(s.dueDate)!==null&&daysUntilDue(s.dueDate)<0).length;
                const rushN=techSamples.filter(s=>s.rush&&parseInt(s.rush)<=2).length;
                return (
                  <div key={tech} style={{background:'white',borderRadius:8,overflow:'hidden',
                    boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
                    border:overdueN>0?'1px solid #b8323233':'1px solid #e2e6f0'}}>
                    <div style={{background:'#1e3060',color:'white',
                      padding:'8px 12px',display:'flex',alignItems:'center',gap:8}}>
                      <div style={{width:28,height:28,borderRadius:'50%',
                        background:'rgba(255,255,255,0.2)',display:'flex',
                        alignItems:'center',justifyContent:'center',
                        fontSize:13,fontWeight:700}}>
                        {tech[0]?.toUpperCase()}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:12}}>{tech}</div>
                        <div style={{fontSize:10,color:'rgba(255,255,255,0.7)'}}>
                          {techSamples.length} sample{techSamples.length!==1?'s':''}
                        </div>
                      </div>
                      {overdueN>0 && (
                        <span style={{background:'#b83232',color:'white',fontSize:9,
                          fontWeight:700,padding:'1px 6px',borderRadius:10}}>
                          {overdueN} overdue
                        </span>
                      )}
                      {rushN>0 && (
                        <span style={{background:'#e09900',color:'white',fontSize:9,
                          fontWeight:700,padding:'1px 6px',borderRadius:10}}>
                          {rushN} rush
                        </span>
                      )}
                    </div>
                    <div style={{padding:8,maxHeight:480,overflowY:'auto'}}>
                      {techSamples
                        .sort((a,b)=>(daysUntilDue(a.dueDate)??999)-(daysUntilDue(b.dueDate)??999))
                        .map(s=>(
                          <SampleCard key={s.id} s={s} compact={compact}
                            onMove={handleMove} onNavigate={onNavigate}
                            onSelectSample={onSelectSample}/>
                        ))
                      }
                    </div>
                    <div style={{borderTop:'1px solid #f0f2f8',padding:'5px 10px',
                      background:'#f8f9ff',fontSize:10,color:'#888',
                      display:'flex',gap:10,flexWrap:'wrap'}}>
                      {Object.entries(
                        techSamples.reduce((m,s)=>{
                          m[s.testType]=(m[s.testType]||0)+1;return m;
                        },{})
                      ).map(([t,n])=>(
                        <span key={t} style={{display:'flex',alignItems:'center',gap:3}}>
                          <span style={{width:7,height:7,borderRadius:2,
                            background:tc(t),display:'inline-block'}}/>
                          {t}: {n}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
              {byTech.length===0 && (
                <div style={{gridColumn:'1/-1',textAlign:'center',
                  color:'#999',padding:40,fontSize:14}}>
                  🎉 No active samples in the lab
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
